import React from 'react';

// Windowed page-number list: always shows first, last, current ± 1, and
// collapses everything else behind an ellipsis — e.g. for 20 pages on
// page 10: [1, '…', 9, 10, 11, '…', 20]. Keeps the control usable even
// when a wide 4K viewport with a large page size still yields many pages.
function pageWindow(page, totalPages) {
  const pages = [];
  const add = (p) => pages.push(p);
  const addGap = () => { if (pages[pages.length - 1] !== '…') pages.push('…'); };

  add(1);
  for (let p = page - 1; p <= page + 1; p++) {
    if (p <= 1 || p >= totalPages) continue;
    if (p === page - 1 && p > 2) addGap();
    add(p);
  }
  if (totalPages > 1) {
    if (page + 1 < totalPages - 1) addGap();
    add(totalPages);
  }
  return pages;
}

// Drop-in list-view footer: "Showing X to Y of Z entries" + an optional
// "Show: [n]" page-size select + prev/numbered/next controls. Mirrors the
// .table-footer/.page-btn pattern already used in TruckList/CustomerList/etc.
// so it looks native, but adds numbered pages instead of just prev/current/next.
export default function Pagination({
  page,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}) {
  // Renders even at a single page whenever there's an item count to show —
  // consumers that measure this footer's real rendered position (e.g.
  // Orders.jsx's viewport-fit row calculation) rely on it always being
  // present, not popping in/out as totalPages crosses 1.
  if (totalPages <= 1 && !onPageSizeChange && totalItems == null) return null;

  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  return (
    <div className="pagination-footer">
      <div className="pagination-info">
        {totalItems != null && (
          <span>Showing {from} to {to} of {totalItems} entries</span>
        )}
        {onPageSizeChange && (
          <label className="pagination-size-label">
            Show:
            <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}>
              {(pageSizeOptions || [10, 25, 50]).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination-numbers">
          <button type="button" className="page-btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)} title="Previous page">←</button>
          {pageWindow(page, totalPages).map((p, i) => p === '…'
            ? <span key={`gap-${i}`} className="page-btn pagination-ellipsis">…</span>
            : <button key={p} type="button" className={'page-btn' + (p === page ? ' active' : '')} onClick={() => onPageChange(p)}>{p}</button>
          )}
          <button type="button" className="page-btn" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} title="Next page">→</button>
        </div>
      )}
    </div>
  );
}
